import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDashboard } from '@/shared/api/base-api';
import { handleApiError, GET, POST } from '@/shared/api/requests';
import { DashboardData } from '@/types/base';
import { BaseLayout } from '@/components/layout';
import { LockKeyhole } from 'lucide-react';
import { useUser } from '@/shared/hooks/useUser';

// Import shadcn/ui components
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Import types from base instead of defining locally
import { Client, Project, InvitedProject } from '@/types/base';

const ProjectCard: React.FC<{ 
  project: Project | InvitedProject; 
  client: Client; 
  isInvited?: boolean;
  hasSubscription?: boolean;
  onSubscriptionClick?: () => void;
}> = ({ project, client, isInvited = false, hasSubscription = true, onSubscriptionClick }) => {
  const navigate = useNavigate();
  const hasDirectory = (directory?: string) => Boolean(directory && directory !== "None");
  
  const canAccess = (directory?: string, permission?: boolean) => {
    if (isInvited) {
      return hasDirectory(directory) && Boolean(permission);
    }
    return hasDirectory(directory);
  };
  
  // Check if warranty is still active
  const isWarrantyActive = () => {
    if (!project.warranty_end_date) return false;
    const warrantyEndDate = new Date(project.warranty_end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day for fair comparison
    return warrantyEndDate >= today;
  };
  
  // Check if project is completed and locked (no subscription and warranty expired)
  const isLocked = !project.is_active && !hasSubscription && !isWarrantyActive();

  // Type guard to check if project is InvitedProject
  const isInvitedProject = (project: Project | InvitedProject): project is InvitedProject => {
    return 'can_access_asbuilds' in project;
  };

  const TagBadge: React.FC<{ 
    href?: string; 
    isActive: boolean; 
    children: React.ReactNode;
    external?: boolean;
  }> = ({ href, isActive, children, external = false }) => {
    const badgeContent = (
        <Badge 
          variant={isActive ? "default" : "secondary"}
          className={`cursor-pointer transition-all duration-200 ${
            isActive 
              ? "bg-accent text-accent-foreground hover:bg-accent/80 hover:text-brand-orange border-border" 
              : "bg-muted text-muted-foreground cursor-not-allowed border-border"
          }`}
        >
        {children}
      </Badge>
    );
    
    if (!isActive || !href) {
      return badgeContent;
    }
    
    if (external) {
      return (
        <a 
          href={href} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="hover:scale-105 transform transition-transform duration-200"
        >
          {badgeContent}
        </a>
      );
    }
    
    // Use React Router navigation for internal links
    return (
      <div 
        onClick={() => navigate(href)}
        className="hover:scale-105 transform transition-transform duration-200 cursor-pointer"
      >
        {badgeContent}
      </div>
    );
  };

  return (
    <Card className={`border-t border-border first:border-t-0 rounded-none border-x-0 border-b-0 shadow-none ${isLocked ? 'bg-muted' : 'bg-card'}`}>
      <CardContent className="grid grid-cols-1 lg:grid-cols-8 gap-6 lg:gap-32 items-center py-12 px-0 relative">
        {/* Lock Overlay for Completed Projects without Subscription */}
        {isLocked && (
          <div className="absolute inset-0 bg-foreground/10 backdrop-blur-[1px] z-10 flex items-center justify-center">
            <button
              onClick={(e) => {
                e.preventDefault();
                onSubscriptionClick?.();
              }}
              className="flex flex-col items-center gap-3 bg-card px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 group"
            >
              <LockKeyhole className="w-12 h-12 text-muted-foreground group-hover:text-primary transition-colors" />
              <div className="text-center">
                <p className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                  Unlock Project Access
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Subscribe to view completed project data
                </p>
              </div>
            </button>
          </div>
        )}
        
        {/* Project Info - Full width on mobile, Left on desktop */}
        <div className="order-2 lg:order-1 lg:col-span-4">
          <div className="space-y-4">
            {/* Project Name */}
            <a className="block">
              <h3 className={`text-2xl font-bold transition-colors ${isLocked ? 'text-muted-foreground' : 'text-foreground hover:text-primary'}`}>
                {project.name}
              </h3>
            </a>
            
            {/* Address */}
            {project.address && project.address !== "None" && (
              <a 
                href={isLocked ? undefined : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(project.address)}`}
                className={`text-base transition-colors block ${isLocked ? 'text-muted-foreground cursor-default' : 'text-muted-foreground hover:text-primary'}`}
                target={isLocked ? undefined : "_blank"}
                rel={isLocked ? undefined : "noopener noreferrer"}
                onClick={(e) => isLocked && e.preventDefault()}
              >
                {project.address}
              </a>
            )}
            
            {/* Tag Badges */}
            <div className="flex flex-wrap gap-2 mt-4">
              <TagBadge
                href={!isLocked && canAccess(project.progress_directory, isInvitedProject(project) ? project.can_access_progress : undefined) 
                  ? `/app/property/${client.name}/${project.id}/progress` 
                  : undefined}
                isActive={!isLocked && canAccess(project.progress_directory, isInvitedProject(project) ? project.can_access_progress : undefined)}
              >
                In Progress
              </TagBadge>
              
              <TagBadge
                href={!isLocked && canAccess(project.asbuilds_directory, isInvitedProject(project) ? project.can_access_asbuilds : undefined) 
                  ? `/app/property/${client.name}/${project.id}/asbuilds` 
                  : undefined}
                isActive={!isLocked && canAccess(project.asbuilds_directory, isInvitedProject(project) ? project.can_access_asbuilds : undefined)}
              >
                As Builds
              </TagBadge>
              
              <TagBadge
                href={!isLocked && canAccess(project.drones_directory, isInvitedProject(project) ? project.can_access_drones : undefined) 
                  ? `/app/property/${client.name}/${project.id}/drones` 
                  : undefined}
                isActive={!isLocked && canAccess(project.drones_directory, isInvitedProject(project) ? project.can_access_drones : undefined)}
              >
                Drone 360 Video
              </TagBadge>
              
              <TagBadge
                href={!isLocked && project.drone_photos_link && (!isInvited || (isInvitedProject(project) && project.can_access_drone_photos)) 
                  ? project.drone_photos_link 
                  : undefined}
                isActive={!isLocked && hasDirectory(project.drone_photos_link) && (!isInvited || !!(isInvitedProject(project) && project.can_access_drone_photos))}
                external
              >
                Drone Photos
              </TagBadge>
              
              <TagBadge
                href={!isLocked && project.has_surveillance && (!isInvited || (isInvitedProject(project) && project.can_access_surveillance)) 
                  ? `/app/surveillance/${client.name}/${project.name}` 
                  : undefined}
                isActive={!isLocked && project.has_surveillance && (!isInvited || !!(isInvitedProject(project) && project.can_access_surveillance))}
              >
                Surveillance
              </TagBadge>
              
              {!isInvited && (
                <TagBadge
                  href={!isLocked ? `/app/team/${client.name}/${project.name}` : undefined}
                  isActive={!isLocked}
                >
                  Project Team
                </TagBadge>
              )}
              
              <TagBadge
                href={!isLocked ? `/app/thermal-preview/${client.name}` : undefined}
                isActive={!isLocked}
              >
                Thermal
              </TagBadge>
              
              <TagBadge
                href={!isLocked ? `/app/property/${client.name}/${project.id}/punch-lists` : undefined}
                isActive={!isLocked}
              >
                Punch Lists
              </TagBadge>
              
              <TagBadge href={undefined} isActive={false}>
                RFIs
              </TagBadge>
              
              <TagBadge href={undefined} isActive={false}>
                Drawings
              </TagBadge>
            </div>
            
            {/* View Project Button */}
            <div className="pt-4 border-t border-border mt-8">
              <Button variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80 hover:bg-transparent">
                <span>View project</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="ml-2 transition-transform group-hover:translate-x-1">
                  <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.5"></path>
                </svg>
              </Button>
            </div>
          </div>
        </div>

        {/* Project Thumbnail - Top on mobile, Right on desktop */}
        <div className="order-1 lg:order-2 w-full lg:col-span-4">
          {project.thumbnail && (
            <div className="relative">
              <a 
                href={project.thumbnail} 
                data-lightbox={`${project.name}-box`} 
                data-title={project.name} 
                className="block w-full"
              >
                  <div className="w-full aspect-[4/3] bg-muted overflow-hidden rounded-lg">
                  <img 
                    src={project.thumbnail} 
                    alt={`${project.name} thumbnail`} 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </a>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

interface SubscriptionPlan {
  id: number;
  name: string;
  description: string;
  price: string;
  currency: string;
  frequency: string;
}

const Dashboard: React.FC = () => {
  const { clientName } = useParams<{ clientName: string }>();
  const { user } = useUser();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [projectSubscriptions, setProjectSubscriptions] = useState<Map<number, boolean>>(new Map());
  
  // Staff and superusers bypass subscription requirements
  const bypassesSubscription = user?.is_superuser || user?.is_staff;

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!clientName) return;
      
      try {
        const data = await getDashboard(clientName);
        setDashboardData(data);
        
        // Set initial tab based on whether there are active projects
        setActiveTab(data.has_active_projects ? 'active' : 'completed');
      } catch (err: unknown) {
        const errorMessage = handleApiError(err);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [clientName]);

  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      setPlansLoading(true);
      try {
        const response = await GET('subscriptions/api/plans/');
        setSubscriptionPlans(response.data.plans || []);
      } catch (err) {
        console.error('Error fetching subscription plans:', err);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchSubscriptionPlans();
  }, []);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      if (!dashboardData) return;
      
      try {
        const response = await GET('subscriptions/my-subscriptions/');
        const subscriptions = response.data.subscriptions || [];
        
        // Build map of project_id -> hasSubscription
        const subMap = new Map<number, boolean>();
        subscriptions.forEach((sub: any) => {
          if (sub.is_active && sub.has_access) {
            subMap.set(sub.project_id, true);
          }
        });
        setProjectSubscriptions(subMap);
      } catch (err) {
        console.error('Error fetching subscriptions:', err);
      }
    };

    fetchSubscriptions();
  }, [dashboardData]);

  // Handle all non-success states with BaseLayout
  if (loading || error || !dashboardData) {
    return (
      <BaseLayout 
        isLoading={loading}
        loadingMessage="Loading your dashboard..."
      >
        {error && (
          <div className="min-h-screen bg-background flex items-center justify-center">
            <div className="text-destructive text-lg">Error: {error}</div>
          </div>
        )}
        {!loading && !error && !dashboardData && (
          <div className="min-h-screen bg-background flex items-center justify-center">
            <div className="text-lg text-muted-foreground">No dashboard data available</div>
          </div>
        )}
      </BaseLayout>
    );
  }

  const handleSubscriptionClick = (project: Project | InvitedProject) => {
    setSelectedProject(project as Project);
    setShowSubscriptionDialog(true);
  };

  const handleCreateSubscription = async (planId: number) => {
    if (!selectedProject || !dashboardData) return;
    
    setCreatingCheckout(true);
    try {
      const response = await POST('subscriptions/api/checkout-session/', {
        plan_id: planId,
        project_id: selectedProject.id,
        client_id: dashboardData.client.id,
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Error creating subscription:', err);
      alert('Failed to create subscription. Please try again.');
    } finally {
      setCreatingCheckout(false);
    }
  };

  const allProjects = [...dashboardData.projects, ...dashboardData.invited_projects];
  const activeProjects = allProjects.filter(p => p.is_active);
  
  // Helper function to check if warranty is active for a project
  const isProjectWarrantyActive = (project: Project | InvitedProject) => {
    if (!project.warranty_end_date) return false;
    const warrantyEndDate = new Date(project.warranty_end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return warrantyEndDate >= today;
  };
  
  // Sort completed projects: unlocked (with subscription or active warranty) first
  const completedProjects = allProjects
    .filter(p => !p.is_active)
    .sort((a, b) => {
      const aHasSubscription = bypassesSubscription || projectSubscriptions.get(a.id) === true;
      const bHasSubscription = bypassesSubscription || projectSubscriptions.get(b.id) === true;
      const aIsUnlocked = aHasSubscription || isProjectWarrantyActive(a);
      const bIsUnlocked = bHasSubscription || isProjectWarrantyActive(b);
      
      // Unlocked projects come first (return -1 if a is unlocked and b is not)
      if (aIsUnlocked && !bIsUnlocked) return -1;
      if (!aIsUnlocked && bIsUnlocked) return 1;
      return 0; // Keep original order if both are locked or both are unlocked
    });

  return (
    <BaseLayout>
      <div className="min-h-screen bg-background mt-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'active' | 'completed')}>
            {/* Centered Toggle Buttons - Smaller Width */}
            <div className="flex justify-center mb-8 mt-10">
              <TabsList className="grid grid-cols-2 h-12 bg-muted border border-border rounded-lg p-1 w-80">
                <TabsTrigger 
                  value="active" 
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-muted-foreground font-medium"
                >
                  Active Projects
                </TabsTrigger>
                <TabsTrigger 
                  value="completed"
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-muted-foreground font-medium"
                >
                  Completed Projects
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Wide Content Area - 100% Width */}
            <div className="w-full max-w-none">
              <div className="mx-auto" style={{ width: '100%' }}>
                <Card className="bg-card rounded-lg shadow-sm">
                  <CardContent className="p-6">
                    <TabsContent value="active" className="mt-0">
                      {activeProjects.length === 0 ? (
                        <div className="text-center py-16">
                          <p className="text-lg text-muted-foreground">
                            0 Active Projects and {dashboardData.completed_count} Completed Projects
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="divide-y divide-border">
                            {activeProjects.map(project => {
                              const isInvited = dashboardData.invited_projects.some(p => p.id === project.id);
                              const hasSubscription = bypassesSubscription || projectSubscriptions.get(project.id) === true || project.is_active;
                              return (
                                <ProjectCard
                                  key={project.id}
                                  project={project}
                                  client={dashboardData.client}
                                  isInvited={isInvited}
                                  hasSubscription={hasSubscription}
                                  onSubscriptionClick={() => handleSubscriptionClick(project)}
                                />
                              );
                            })}
                          </div>
                          
                          {/* View All Button */}
                          <div className="text-center pt-12 mt-12 border-t border-border">
                            <Button variant="outline" className="px-6 py-3">
                              View All Projects
                            </Button>
                          </div>
                        </>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="completed" className="mt-0">
                      {completedProjects.length === 0 ? (
                        <div className="text-center py-16">
                          <p className="text-lg text-muted-foreground">
                            {dashboardData.active_count} Active Projects and 0 Completed Projects
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="divide-y divide-border">
                            {completedProjects.map(project => {
                              const isInvited = dashboardData.invited_projects.some(p => p.id === project.id);
                              const hasSubscription = bypassesSubscription || projectSubscriptions.get(project.id) === true;
                              return (
                                <ProjectCard
                                  key={project.id}
                                  project={project}
                                  client={dashboardData.client}
                                  isInvited={isInvited}
                                  hasSubscription={hasSubscription}
                                  onSubscriptionClick={() => handleSubscriptionClick(project)}
                                />
                              );
                            })}
                          </div>
                          
                          {/* View All Button */}
                          <div className="text-center pt-12 mt-12 border-t border-border">
                            <Button variant="outline" className="px-6 py-3">
                              View All Projects
                            </Button>
                          </div>
                        </>
                      )}
                    </TabsContent>
                  </CardContent>
                </Card>
              </div>
            </div>
          </Tabs>
        </div>
      </div>

      {/* Subscription Dialog */}
      <Dialog open={showSubscriptionDialog} onOpenChange={setShowSubscriptionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Subscribe to Access Project</DialogTitle>
            <DialogDescription>
              {selectedProject && (
                <>
                  Subscribe to access <strong>{selectedProject.name}</strong> and all its completed project data.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Choose a subscription plan to unlock access to this completed project's documents, photos, and historical data.
            </p>
            
            {plansLoading ? (
              <div className="text-center py-8">
                <div className="text-sm text-muted-foreground">Loading plans...</div>
              </div>
            ) : subscriptionPlans.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-sm text-muted-foreground">No subscription plans available at this time.</div>
              </div>
            ) : (
              <div className="space-y-3">
                {subscriptionPlans.map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => handleCreateSubscription(plan.id)}
                    disabled={creatingCheckout}
                    className="w-full p-4 border border-border rounded-lg hover:border-primary hover:bg-accent/30 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="font-semibold text-base text-foreground flex-1">
                          {plan.name}
                        </h4>
                        <div className="text-right flex-shrink-0">
                          <div className="font-bold text-lg text-primary">
                            ${plan.price}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            /{plan.frequency === 'month' ? 'month' : 'year'}
                          </div>
                        </div>
                      </div>
                      {plan.description && (
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {plan.description}
                        </p>
                      )}
                      {plan.frequency === 'year' && (
                        <div className="text-xs text-green-600 font-medium">
                          💰 Save 17% with annual billing
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {creatingCheckout && (
              <div className="text-center text-sm text-muted-foreground">
                Redirecting to Stripe...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </BaseLayout>
  );
};

export default Dashboard;